// Decision logic for where the bare "/" entrypoint sends an already-authenticated,
// onboarded user. Kept as a pure function (no session/orgs fetches, no redirect
// side effects) so every branch is unit-testable in isolation — bounceToActiveOrg
// in router.tsx does the async fetching and turns a RootDestination into a redirect.

// The only field the decision needs from an org (the full row type lives in api.ts).
export interface RootRedirectOrg {
  id: string;
}

export type RootDestination =
  | { kind: "org"; orgId: string }
  | { kind: "choose" }
  | { kind: "onboarding" };

/**
 * Resolve the "/" landing from the session's active org and the viewer's own
 * member orgs. Pure and total: a failed membership fetch is NOT modeled here — the
 * caller (bounceToActiveOrg) lets that error surface to a retryable error screen
 * instead of guessing a destination.
 *
 * @param active The session's activeOrganizationId (or null/undefined if none).
 * @param orgs   The viewer's OWN member orgs (successfully loaded).
 *
 * Why the membership check matters: the /orgs/$orgId guard calls setActive on every
 * visit and super-admins are admitted to ANY org, so merely opening a customer org
 * writes that foreign org onto the session. If we honored `active` blindly, every
 * "/" hit would dump the admin back into that customer's workspace (the bug Madhur
 * hit — viewed Joy Life, "/" kept landing there). So `active` is honored only when
 * it's one of the viewer's own memberships.
 */
export function resolveRootRedirect(
  active: string | null | undefined,
  orgs: RootRedirectOrg[],
): RootDestination {
  // Resume the last org only when the viewer actually belongs to it.
  if (active && orgs.some((o) => o.id === active)) {
    return { kind: "org", orgId: active };
  }

  // Exactly one own org: go straight there (also heals a stale foreign active org,
  // since the /orgs/$orgId guard re-runs setActive on arrival).
  if (orgs.length === 1) {
    return { kind: "org", orgId: orgs[0].id };
  }

  // No own memberships: honor a foreign active org if present (a super-admin whose
  // only association is a customer org) rather than dumping an already-onboarded
  // user on /onboarding, which loops back to "/". Otherwise it's a genuinely new
  // user → onboarding.
  if (orgs.length === 0) {
    return active ? { kind: "org", orgId: active } : { kind: "onboarding" };
  }

  // Multiple own orgs and no valid active one — let the viewer choose.
  return { kind: "choose" };
}
